import type { Card } from '@/types/card';
import { base64EncodeFile } from '@/utils/fileToBase64';
import toast from 'react-hot-toast';

export const useVCardGenerator = (card: Card | null) => {
  // Fonction pour redimensionner une image avant de l'encoder en base64
  const resizeAndEncodeImage = async (imageUrl: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        // Redimensionner l'image à une taille raisonnable pour les vCards (max 300x300px)
        const maxSize = 300;
        let width = img.width;
        let height = img.height;

        if (width > height && width > maxSize) {
          height = Math.round((height * maxSize) / width);
          width = maxSize;
        } else if (height > maxSize) {
          width = Math.round((width * maxSize) / height);
          height = maxSize;
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Impossible de créer le contexte canvas'));
          return;
        }

        // Dessiner l'image redimensionnée sur le canvas
        ctx.drawImage(img, 0, 0, width, height);

        // Convertir en base64 avec une qualité réduite (0.7 = 70%)
        const base64 = canvas.toDataURL('image/jpeg', 0.7).split(',')[1];
        resolve(base64);
      };

      img.onerror = () => {
        reject(new Error("Erreur lors du chargement de l'image"));
      };

      img.src = imageUrl;
    });
  };

  // Function to generate vCard content without downloading
  const generateVCardContent = async (): Promise<string> => {
    if (!card) return '';

    let profileBase64 = '';
    let photoProperty = '';

    if (card.avatar) {
      try {
        // Si c'est une URL, essayer de redimensionner l'image d'abord
        if (typeof card.avatar === 'string') {
          try {
            profileBase64 = await resizeAndEncodeImage(card.avatar);
          } catch (resizeError) {
            // Fallback à la méthode standard si le redimensionnement échoue
            profileBase64 = await base64EncodeFile(card.avatar);
          }
        } else {
          // Pour les objets File, utiliser la méthode standard
          profileBase64 = await base64EncodeFile(card.avatar);
        }

        // Vérifier si la conversion a réussi
        if (profileBase64) {
          // Utiliser le format vCard 3.0 standard pour les photos
          photoProperty = `PHOTO;ENCODING=b;TYPE=JPEG:${profileBase64}`;
        }
      } catch (error) {
        console.error("Erreur lors de la conversion de l'avatar:", error);
      }
    }

    const formatPhoneNumber = (phone: string): string => {
      return phone.replace(/^(tel:|[^+0-9])/g, '');
    };

    const vCardContent = `BEGIN:VCARD
VERSION:3.0
N:${card.full_name};;;;
FN:${card.full_name}
ORG:${card.company || ''}
TITLE:${card.job || ''}
${card.links?.find(link => link.icon?.toLowerCase() === 'phone')?.url ? `TEL;TYPE=CELL:${formatPhoneNumber(card.links?.find(link => link.icon?.toLowerCase() === 'phone')?.url || '')}` : ''}
${photoProperty}
${card.links
  ?.filter(link => link.icon?.toLowerCase() !== 'phone')
  .map(link => {
    const cleanUrl = link.url.replace(/^(mailto:|tel:)/, '');
    switch (link.icon?.toLowerCase()) {
      case 'safari':
        return `URL:${cleanUrl}`;
      case 'mail':
        return `EMAIL;TYPE=WORK:${cleanUrl}`;
      case 'call':
        return `TEL:${cleanUrl}`;
      case 'whatsapp':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'linkedin':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'instagram':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'x':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'telegram':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'discord':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'youtube':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'tiktok':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'snapchat':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'threads':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'spotify':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'soundcloud':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'twitch':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'pinterest':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'paypal':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'opensea':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'calendly':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'zoho':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'address':
        return `ADR:;;${cleanUrl};;;;`;
      case 'message':
        return `TEL:${cleanUrl}`;
      case 'app_link':
        return `URL:${cleanUrl}`;
      case 'apple_music':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'cal':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'google_reviews':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      default:
        return '';
    }
  })
  .filter(Boolean)
  .join('\n')}
NOTE:${card.biography || ''}
END:VCARD`.trim();

    return vCardContent;
  };

  const generateVCard = async () => {
    if (!card) return '';

    const vCardContent = await generateVCardContent();

    try {
      const blob = new Blob([vCardContent], { type: 'text/vcard' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${card.full_name?.toLowerCase().replace(/\s+/g, '_')}.vcf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erreur lors du téléchargement de la carte de visite');
      console.error('Erreur lors de la génération de la vCard:', error);
    }

    return vCardContent;
  };

  // Function to generate vCard content for QR codes with all links but no photo
  const generateSimplifiedVCardContent = async (): Promise<string> => {
    if (!card) return '';

    const formatPhoneNumber = (phone: string): string => {
      return phone.replace(/^(tel:|[^+0-9])/g, '');
    };

    return `BEGIN:VCARD
VERSION:3.0
N:${card.full_name};;;;
FN:${card.full_name}
ORG:${card.company || ''}
TITLE:${card.job || ''}
${card.links?.find(link => link.icon?.toLowerCase() === 'phone')?.url ? `TEL;TYPE=CELL:${formatPhoneNumber(card.links?.find(link => link.icon?.toLowerCase() === 'phone')?.url || '')}` : ''}
${card.links
  ?.filter(link => link.icon?.toLowerCase() !== 'phone')
  .map(link => {
    const cleanUrl = link.url.replace(/^(mailto:|tel:)/, '');
    switch (link.icon?.toLowerCase()) {
      case 'safari':
        return `URL:${cleanUrl}`;
      case 'mail':
        return `EMAIL;TYPE=WORK:${cleanUrl}`;
      case 'call':
        return `TEL:${cleanUrl}`;
      case 'whatsapp':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'linkedin':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'instagram':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'x':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'telegram':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'discord':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'youtube':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'tiktok':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'snapchat':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'threads':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'spotify':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'soundcloud':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'twitch':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'pinterest':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'paypal':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'opensea':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'calendly':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'zoho':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'address':
        return `ADR:;;${cleanUrl};;;;`;
      case 'message':
        return `TEL:${cleanUrl}`;
      case 'app_link':
        return `URL:${cleanUrl}`;
      case 'apple_music':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'cal':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      case 'google_reviews':
        return `X-SOCIALPROFILE;TYPE=${link.title}:${cleanUrl}`;
      default:
        return '';
    }
  })
  .filter(Boolean)
  .join('\n')}
NOTE:${card.biography || ''}
END:VCARD`.trim();
  };

  // Return all functions
  return {
    generateVCard,
    generateVCardContent,
    generateSimplifiedVCardContent,
  };
};
